<template>
  <el-card class="json-viewer-card">
    <template #header>
      <div class="card-header">
        <div class="header-left">
          <span class="card-title">JSON Output</span>
        </div>
        <div class="controls">
          <div class="button-group control-group">
            <el-button size="small" @click="toggleAllNodes" class="control-btn">
              <span class="btn-icon">{{ allExpanded ? '📂' : '📁' }}</span>
              {{ allExpanded ? 'Collapse All' : 'Expand All' }}
            </el-button>
            <el-button size="small" @click="setWrapJson" class="control-btn">
              <span class="btn-icon">{{ isWrappedJson ? '📄' : '📝' }}</span>
              {{ buttonText }}
            </el-button>
          </div>
          <div class="button-divider"></div>
          <div class="button-group action-group">
            <el-button type="primary" size="small" @click="copyJson" class="action-btn">
              <span class="btn-icon">📋</span>
              Copy
            </el-button>
            <el-button type="success" size="small" @click="convertToCsv" class="action-btn">
              <span class="btn-icon">📊</span>
              To CSV
            </el-button>
            <el-button
              v-if="csvDownloadLink"
              type="success"
              size="small"
              @click="downloadCsv"
              class="action-btn"
            >
              <span class="btn-icon">⬇️</span>
              Download
            </el-button>
          </div>
        </div>
      </div>
    </template>
    <div class="json-pretty-container">
      <vue-json-pretty
        v-if="parseJsonData !== ''"
        :key="treeKey"
        :data="parseJsonData"
        :show-line-number="false"
        :showSelectController="true"
        :collapsed-on-click-brackets="true"
        :deep="deepLevel"
        :show-double-quotes="!isWrappedJson"
        :show-length="true"
        :virtual="false"
      />
      <div v-else class="empty-placeholder">
        <span class="empty-icon">📭</span>
        <span class="empty-text">No data</span>
      </div>
    </div>
  </el-card>
</template>

<script>
import VueJsonPretty from "vue-json-pretty";
import "vue-json-pretty/lib/styles.css";

export default {
  name: "JsonViewer",
  components: {
    VueJsonPretty,
  },
  props: {
    jsonData: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      csvDownloadLink: null,
      isWrappedJson: false,
      allExpanded: true,
      deepLevel: Infinity,
      treeKey: 0,
    };
  },
  computed: {
    buttonText() {
      return this.isWrappedJson ? "Unwrap" : "Wrap";
    },
    parseJsonData() {
      // 如果是包裹的JSON，直接返回原始数据
      if (!this.jsonData || this.jsonData.trim() === "") {
        return "";
      }
      // 尝试解析JSON数据
      try {
        if (this.isWrappedJson) {
          return this.jsonData;
        } else {
          return JSON.parse(this.jsonData);
        }
      } catch (e) {
        console.error("Invalid JSON data:", e);
        return "";
      }
    },
  },

  methods: {
    copyJson() {
      let jsonText = JSON.stringify(this.parseJsonData, null, 2);
      navigator.clipboard
        .writeText(jsonText)
        .then(() => {
          console.log("JSON copied to clipboard!");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
        });
    },
    setWrapJson() {
      this.isWrappedJson = !this.isWrappedJson;
    },
    toggleAllNodes() {
      if (this.allExpanded) {
        // 折叠全部：只展开第一层
        this.deepLevel = 1;
        this.allExpanded = false;
      } else {
        // 展开全部：展开所有层级
        this.deepLevel = Infinity;
        this.allExpanded = true;
      }
      // 强制重新渲染组件以应用新的 deep 值
      this.treeKey++;
    },

    convertToCsv() {
      try {
        const jsonData = JSON.parse(this.jsonData);
        if (!Array.isArray(jsonData)) {
          alert("JSON data should be an array of objects.");
          return;
        }

        const csvRows = [];
        const headers = Object.keys(jsonData[0]);
        csvRows.push(headers.join(","));

        for (const row of jsonData) {
          const values = headers.map((header) => {
            let val = row[header];
            if (typeof val === "undefined" || val === null) {
              val = "";
            }
            return typeof val === "string"
              ? `"${val.replace(/"/g, '""')}"`
              : val;
          });
          csvRows.push(values.join(","));
        }

        const csvString = csvRows.join("\n");
        this.csvDownloadLink = this.createCsvDownloadLink(csvString);
      } catch (error) {
        alert("Invalid JSON input.");
      }
    },
    createCsvDownloadLink(csvString) {
      const blob = new Blob([csvString], { type: "text/csv" });
      return URL.createObjectURL(blob);
    },
    downloadCsv() {
      const link = document.createElement("a");
      link.href = this.csvDownloadLink;
      link.download = "data.csv";
      link.click();
    },
  },
};
</script>

<style scoped>
.json-viewer-card {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  background: var(--bg-card);
  border: 1px solid var(--border-light);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-light);
}

.header-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.card-title {
  margin: 0;
  font-family: var(--font-heading);
  font-weight: 600;
  color: var(--text-primary);
  font-size: var(--text-xl);
}

.controls {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.button-group {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.control-group {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.action-group {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.button-divider {
  width: 1px;
  height: 24px;
  background: var(--border-color);
  margin: 0 var(--spacing-xs);
}

.control-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  font-weight: 500;
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.control-btn:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  font-weight: 500;
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
  opacity: 0.9;
}

.btn-icon {
  font-size: 14px;
}

.json-pretty-container {
  flex: 1;
  min-height: 400px;
  max-height: calc(100vh - 220px);
  overflow: auto;
  padding: var(--spacing-md);
  font-family: var(--font-mono);
  background: var(--bg-card);
  border-radius: var(--radius-md);
}

:deep(.vjs-tree) {
  font-family: var(--font-mono);
  font-size: 14px;
  line-height: 1.9;
}

/* 键名使用主题色 */
:deep(.vjs-tree .vjs-key) {
  color: var(--color-accent);
  font-weight: 500;
}

/* 字符串值 */
:deep(.vjs-tree .vjs-value__string) {
  color: var(--color-success);
}

/* 数字值 */
:deep(.vjs-tree .vjs-value__number) {
  color: var(--color-warning);
}

/* 布尔值 */
:deep(.vjs-tree .vjs-value__boolean) {
  color: var(--color-error);
}

/* null 值 */
:deep(.vjs-tree .vjs-value__null) {
  color: var(--text-disabled);
  font-style: italic;
}

/* 括号样式优化 */
:deep(.vjs-tree .vjs-tree__brackets) {
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-weight: 600;
  font-size: 15px;
}

:deep(.vjs-tree .vjs-tree__brackets:hover) {
  color: var(--color-primary);
  transform: scale(1.15);
}

/* 折叠图标样式 */
:deep(.vjs-tree .vjs-tree__icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 4px;
  cursor: pointer;
  transition: all var(--transition-fast);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: bold;
}

:deep(.vjs-tree .vjs-tree__icon:hover) {
  color: var(--color-primary);
  background: var(--color-accent-soft);
  border-radius: var(--radius-sm);
  transform: scale(1.2);
}

/* 展开状态的图标 */
:deep(.vjs-tree .vjs-tree__icon.vjs-tree__icon--expanded) {
  color: var(--color-accent);
}

/* 每一层级的缩进线 */
:deep(.vjs-tree .vjs-tree__node) {
  position: relative;
  padding: 2px 0;
  transition: background var(--transition-fast);
  border-radius: var(--radius-sm);
}

:deep(.vjs-tree .vjs-tree__node:hover) {
  background: var(--bg-hover);
}

/* 层级缩进视觉引导线 */
:deep(.vjs-tree .vjs-tree__indent) {
  position: relative;
  display: inline-block;
  width: 20px;
}

:deep(.vjs-tree .vjs-tree__indent::before) {
  content: '';
  position: absolute;
  left: 8px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--border-color);
  opacity: 0.3;
}

/* 折叠时显示的长度提示 */
:deep(.vjs-tree .vjs-tree__content-length) {
  margin-left: 8px;
  padding: 2px 8px;
  background: var(--color-accent-soft);
  border-radius: var(--radius-lg);
  font-size: 12px;
  color: var(--color-primary);
  font-weight: 500;
}

/* 对象/数组的整行悬浮效果 */
:deep(.vjs-tree .vjs-tree__brackets-line) {
  transition: all var(--transition-fast);
}

:deep(.vjs-tree .vjs-tree__brackets-line:hover) {
  padding-left: 4px;
}

/* 逗号样式 */
:deep(.vjs-tree .vjs-comma) {
  color: var(--text-disabled);
  margin-left: -2px;
}

/* 冒号样式 */
:deep(.vjs-tree .vjs-colon) {
  color: var(--text-disabled);
  margin: 0 4px;
}

/* 空状态占位符 */
.empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
  gap: var(--spacing-md);
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-text {
  color: var(--text-disabled);
  font-size: var(--text-sm);
  font-style: italic;
}

:deep(.json-viewer-card .el-card__header) {
  border-bottom: 1px solid var(--border-light);
  background: var(--bg-card);
  padding: var(--spacing-md) var(--spacing-lg);
}

@media (max-width: 1024px) {
  .card-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    justify-content: flex-start;
  }

  .controls {
    justify-content: flex-start;
  }

  .button-divider {
    display: none;
  }
}
</style>